import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import type { CoachSessionSelfAttendanceStatus, Lesson, LessonSession } from '@court-sync/shared';
import {
  generatePrivateLessonOccurrenceDates,
  isStudentSessionAttendanceMarked,
  getCoachSelfCheckInRosterBlockReason,
  COACH_SELF_CHECK_IN_NO_STUDENTS_MSG,
  COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG,
} from '@court-sync/shared';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService, type CoachScheduleData, type AttendanceMark } from '@/services/coach';
import { lessonSessionsService } from '@/services/lessonSessions';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { AttendanceToggle } from '@/components/lessons/AttendanceToggle';
import type { AttendanceMarkStatus } from '@/components/lessons/AttendanceToggle';
import { CoachSelfCheckInActions } from '@/components/coach/CoachSelfCheckInActions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { formatTimeHHMM } from '@/utils/timeFormat';
import {
  ClipboardCheck,
  ClipboardList,
  Clock,
  Loader2,
  Users,
} from 'lucide-react';

type AttendanceStatus = AttendanceMarkStatus;

type RosterRow =
  | { kind: 'booking'; id: string; studentId: string; isMakeup: boolean }
  | { kind: 'reservation'; id: string; studentId: string };

function lessonTypeBadge(lessonType: Lesson['lessonType']): {
  label: string;
  className: string;
} {
  switch (lessonType) {
    case 'private':
      return {
        label: 'Private',
        className: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
      };
    case 'camp':
      return {
        label: 'Camp',
        className: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
      };
    default:
      return {
        label: 'Group',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
      };
  }
}

function lessonAccentBorder(lessonType: Lesson['lessonType']): string {
  switch (lessonType) {
    case 'private':
      return 'border-l-purple-500';
    case 'camp':
      return 'border-l-violet-500';
    default:
      return 'border-l-blue-500';
  }
}

function coachSelfAttendanceBadge(
  status: CoachSessionSelfAttendanceStatus | null | undefined
): { label: string; className: string } | null {
  if (!status) return null;
  if (status.checkedIn || status.status === 'attended') {
    return {
      label: 'You checked in',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
    };
  }
  if (status.status === 'missed') {
    return {
      label: 'Missed',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
    };
  }
  if (status.status === 'cancelled') {
    return {
      label: 'Cancelled',
      className: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
    };
  }
  return null;
}

export default function CoachRosterPage() {
  const { academyId, academy } = useCoachAcademyStore();
  const allowSelfCheckIn = academy?.allowCoachSelfAttendance === true;

  const [schedule, setSchedule] = useState<CoachScheduleData | null>(null);
  const [lessonSessionsToday, setLessonSessionsToday] = useState<LessonSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState<
    Record<string, 'scheduled' | AttendanceStatus>
  >({});
  const [markingRowId, setMarkingRowId] = useState<string | null>(null);
  const [selfAttendanceByLessonId, setSelfAttendanceByLessonId] = useState<
    Record<string, CoachSessionSelfAttendanceStatus>
  >({});
  const [loadingSelfByLessonId, setLoadingSelfByLessonId] = useState<Record<string, boolean>>({});
  const [checkingInLessonId, setCheckingInLessonId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayKey = format(today, 'yyyy-MM-dd');
  const todayDayNum = today.getDay();

  const getSessionIdForLesson = useCallback(
    (lessonId: string) =>
      lessonSessionsToday.find(
        (s) =>
          s.lessonId === lessonId &&
          s.sessionDate.replace(/T.*/, '').slice(0, 10) === todayKey
      )?.id,
    [lessonSessionsToday, todayKey]
  );

  useEffect(() => {
    if (!academyId) return;
    setLoading(true);
    Promise.all([
      coachService.getCoachSchedule(academyId, { startDate: todayKey, endDate: todayKey }),
      lessonSessionsService.listByAcademyAndDateRange(academyId, todayKey, todayKey),
    ])
      .then(([data, sessions]) => {
        setSchedule(data);
        setLessonSessionsToday(sessions);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [academyId, todayKey]);

  useEffect(() => {
    if (!schedule?.attendance) return;
    const initial: Record<string, AttendanceStatus> = {};
    for (const [key, records] of Object.entries(schedule.attendance)) {
      const colonIdx = key.lastIndexOf(':');
      if (colonIdx === -1) continue;
      const bookingId = key.slice(0, colonIdx);
      const date = key.slice(colonIdx + 1);
      if (date === todayKey && records.length > 0) {
        const status = records[0].status;
        if (status === 'attended' || status === 'missed' || status === 'cancelled') {
          initial[bookingId] = status;
        }
      }
    }
    setAttendance((prev) => (Object.keys(initial).length > 0 ? { ...initial, ...prev } : prev));
  }, [schedule?.attendance, todayKey]);

  const todaysLessons = useMemo(() => {
    return (schedule?.lessons ?? []).filter((lesson) => {
      if (lesson.lessonType === 'private' && lesson.startDate && lesson.numberOfSessions) {
        const sessionDates = generatePrivateLessonOccurrenceDates(
          lesson.startDate,
          lesson.numberOfSessions,
          lesson.daysOfWeek
        );
        return sessionDates.some((d) => format(d, 'yyyy-MM-dd') === todayKey);
      }

      if (lesson.lessonType === 'camp') {
        if (!lesson.startDate || !lesson.endDate) return false;
        const cs = new Date(lesson.startDate);
        cs.setHours(0, 0, 0, 0);
        const ce = new Date(lesson.endDate);
        ce.setHours(23, 59, 59, 999);
        if (today < cs || today > ce) return false;
        return lesson.daysOfWeek?.includes(todayDayNum) ?? false;
      }

      if (!lesson.daysOfWeek?.includes(todayDayNum)) return false;

      const term = lesson.termId ? schedule?.terms[lesson.termId] : null;
      if (!term) return false;

      const termStart = new Date(term.startDate);
      termStart.setHours(0, 0, 0, 0);
      const termEnd = new Date(term.endDate);
      termEnd.setHours(23, 59, 59, 999);
      if (today < termStart || today > termEnd) return false;

      if (lesson.cancelledFromDate) {
        const cancelledFrom = new Date(lesson.cancelledFromDate);
        cancelledFrom.setHours(0, 0, 0, 0);
        if (today >= cancelledFrom) return false;
      }

      const cancelledToday = schedule?.overrides.some(
        (o) =>
          o.lessonId === lesson.id &&
          o.overrideType === 'cancelled' &&
          new Date(o.originalDate).toISOString().slice(0, 10) === todayKey
      );
      if (cancelledToday) return false;

      return true;
    });
  }, [schedule, today, todayKey, todayDayNum]);

  const getBookingsForLesson = useCallback(
    (lessonId: string) =>
      schedule?.bookings.filter((b) => {
        if (b.lessonId !== lessonId || b.status === 'cancelled') return false;
        if (!b.sessionDate) return true;
        return b.sessionDate.split('T')[0] === todayKey;
      }) ?? [],
    [schedule?.bookings, todayKey]
  );

  const getRosterRowsForLesson = useCallback(
    (lessonId: string): RosterRow[] => {
      const bookings = getBookingsForLesson(lessonId);
      const reservations =
        schedule?.makeupSessionReservations?.filter(
          (r) => r.lessonId === lessonId && r.sessionDate.split('T')[0] === todayKey
        ) ?? [];
      const seenStudents = new Set(bookings.map((b) => b.studentId));
      const rows: RosterRow[] = bookings.map((b) => ({
        kind: 'booking' as const,
        id: b.id,
        studentId: b.studentId,
        isMakeup: !!b.sessionDate,
      }));
      for (const r of reservations) {
        if (seenStudents.has(r.studentId)) continue;
        seenStudents.add(r.studentId);
        rows.push({ kind: 'reservation', id: r.id, studentId: r.studentId });
      }
      return rows;
    },
    [getBookingsForLesson, schedule?.makeupSessionReservations, todayKey]
  );

  const totalStudents = useMemo(
    () => todaysLessons.reduce((sum, lesson) => sum + getRosterRowsForLesson(lesson.id).length, 0),
    [todaysLessons, getRosterRowsForLesson]
  );

  const loadSelfAttendanceForLesson = useCallback(
    async (lessonId: string) => {
      if (!academyId || !allowSelfCheckIn) return;
      setLoadingSelfByLessonId((prev) => ({ ...prev, [lessonId]: true }));
      try {
        const sessionId = getSessionIdForLesson(lessonId);
        const status = await coachService.getMySessionAttendanceStatus({
          academyId,
          lessonId,
          sessionDate: todayKey,
          sessionId,
        });
        setSelfAttendanceByLessonId((prev) => ({ ...prev, [lessonId]: status }));
      } catch {
        setSelfAttendanceByLessonId((prev) => {
          const next = { ...prev };
          delete next[lessonId];
          return next;
        });
      } finally {
        setLoadingSelfByLessonId((prev) => ({ ...prev, [lessonId]: false }));
      }
    },
    [academyId, allowSelfCheckIn, getSessionIdForLesson, todayKey]
  );

  useEffect(() => {
    if (!allowSelfCheckIn || todaysLessons.length === 0) return;
    void Promise.all(todaysLessons.map((lesson) => loadSelfAttendanceForLesson(lesson.id)));
  }, [allowSelfCheckIn, todaysLessons, loadSelfAttendanceForLesson]);

  const handleSelfCheckIn = useCallback(
    async (lessonId: string, status: 'attended' | 'scheduled') => {
      if (!academyId) return;

      if (status === 'attended') {
        const rosterRows = getRosterRowsForLesson(lessonId);
        const markedCount = rosterRows.filter((r) =>
          isStudentSessionAttendanceMarked(attendance[r.id])
        ).length;
        const blockReason = getCoachSelfCheckInRosterBlockReason(rosterRows.length, markedCount);
        if (blockReason === 'no_students') {
          toast.error(COACH_SELF_CHECK_IN_NO_STUDENTS_MSG);
          return;
        }
        if (blockReason === 'incomplete_attendance') {
          toast.error(COACH_SELF_CHECK_IN_REQUIRES_STUDENT_ATTENDANCE_MSG);
          return;
        }
      }

      setCheckingInLessonId(lessonId);
      try {
        await coachService.checkInSelfSession({
          academyId,
          lessonId,
          sessionDate: todayKey,
          sessionId: getSessionIdForLesson(lessonId),
          status,
        });
        toast.success(status === 'attended' ? 'Checked in' : 'Check-in cleared');
        await loadSelfAttendanceForLesson(lessonId);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to update check-in');
      } finally {
        setCheckingInLessonId(null);
      }
    },
    [academyId, todayKey, getSessionIdForLesson, loadSelfAttendanceForLesson, getRosterRowsForLesson, attendance]
  );

  const handleMarkStudent = useCallback(
    async (
      lessonId: string,
      row: RosterRow,
      status: AttendanceStatus | 'scheduled'
    ) => {
      if (!academyId) {
        setError('Please wait for the academy to load.');
        return;
      }

      const previousStatus = attendance[row.id] ?? 'scheduled';
      setMarkingRowId(row.id);
      if (status === 'scheduled') {
        setAttendance((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      } else {
        setAttendance((prev) => ({ ...prev, [row.id]: status }));
      }
      setError(null);

      const record: AttendanceMark =
        row.kind === 'booking'
          ? { bookingId: row.id, studentId: row.studentId, status }
          : {
              makeupSessionReservationId: row.id,
              studentId: row.studentId,
              status,
            };

      try {
        const sessionId = getSessionIdForLesson(lessonId);
        await coachService.markAttendance(academyId, lessonId, todayKey, [record], {
          ...(sessionId ? { sessionId } : {}),
        });
        toast.success(status === 'scheduled' ? 'Attendance cleared' : 'Attendance saved');
      } catch (err) {
        if (previousStatus === 'scheduled') {
          setAttendance((prev) => {
            const next = { ...prev };
            delete next[row.id];
            return next;
          });
        } else {
          setAttendance((prev) => ({ ...prev, [row.id]: previousStatus }));
        }
        setError(err instanceof Error ? err.message : 'Failed to save attendance');
      } finally {
        setMarkingRowId(null);
      }
    },
    [academyId, attendance, getSessionIdForLesson, todayKey]
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">Loading today&apos;s roster…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Today's Roster"
        help={
          <p>
            {today.toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        }
      />

      {todaysLessons.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <DashboardStatCard
            label="Lessons today"
            value={todaysLessons.length}
            icon={<ClipboardList className="text-blue-600 dark:text-blue-400" aria-hidden />}
            iconBg="bg-blue-100 dark:bg-blue-900"
            iconColor="text-blue-600 dark:text-blue-400"
          />
          <DashboardStatCard
            label="Students"
            value={totalStudents}
            icon={<Users className="text-green-600 dark:text-green-400" aria-hidden />}
            iconBg="bg-green-100 dark:bg-green-900"
            iconColor="text-green-600 dark:text-green-400"
          />
        </div>
      )}

      {error && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400"
          role="alert"
        >
          {error}
        </div>
      )}

      {todaysLessons.length === 0 ? (
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={ClipboardList} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
            No lessons today
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            You don&apos;t have any sessions scheduled for today. Check My Schedule for upcoming
            lessons.
          </p>
        </DashboardEmptyStateCard>
      ) : (
        <div className="space-y-4">
          {todaysLessons.map((lesson) => {
            const rosterRows = getRosterRowsForLesson(lesson.id);
            const termName = lesson.termId ? schedule?.terms[lesson.termId]?.name : undefined;
            const typeBadge = lessonTypeBadge(lesson.lessonType);
            const selfAttendance = selfAttendanceByLessonId[lesson.id];
            const selfBadge = coachSelfAttendanceBadge(selfAttendance);
            const loadingSelf = loadingSelfByLessonId[lesson.id] === true;
            const markedStudentCount = rosterRows.filter((r) =>
              isStudentSessionAttendanceMarked(attendance[r.id])
            ).length;

            return (
              <Card
                key={lesson.id}
                className={`overflow-hidden border-l-4 shadow-sm ${lessonAccentBorder(lesson.lessonType)}`}
              >
                <CardHeader className="space-y-2 border-b border-border bg-muted/20 px-4 py-4 sm:px-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-base font-semibold">{lesson.name}</CardTitle>
                        <span
                          className={`inline-flex rounded px-2 py-0.5 text-[11px] font-medium ${typeBadge.className}`}
                        >
                          {typeBadge.label}
                        </span>
                        {selfBadge && (
                          <span
                            className={`inline-flex rounded px-2 py-0.5 text-[11px] font-medium ${selfBadge.className}`}
                          >
                            {selfBadge.label}
                          </span>
                        )}
                      </div>
                      <CardDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {formatTimeHHMM(lesson.startTime)}–{formatTimeHHMM(lesson.endTime)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {rosterRows.length}{' '}
                          {rosterRows.length === 1 ? 'student' : 'students'}
                        </span>
                        {termName && <span>{termName}</span>}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-0">
                  {rosterRows.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-muted-foreground sm:px-6">
                      No enrolled students for this session.
                    </p>
                  ) : (
                    <>
                      <div
                        className="hidden border-b border-border bg-muted/10 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:grid sm:grid-cols-[minmax(0,1fr)_6.5rem] sm:items-center sm:gap-x-4 sm:px-6"
                        aria-hidden
                      >
                        <span>Student</span>
                        <span className="text-center">Attendance</span>
                      </div>
                      <ul className="divide-y divide-border">
                        {rosterRows.map((row) => {
                          const name = row.studentId
                            ? (schedule?.students[row.studentId] ?? 'Unknown')
                            : 'Unknown';
                          const current = attendance[row.id] ?? 'scheduled';
                          const isMakeup =
                            row.kind === 'reservation' ||
                            (row.kind === 'booking' && row.isMakeup);

                          return (
                            <li
                              key={row.id}
                              className="px-4 py-3 sm:grid sm:grid-cols-[minmax(0,1fr)_6.5rem] sm:items-center sm:gap-x-4 sm:px-6"
                            >
                              <div className="mb-2 min-w-0 sm:mb-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-foreground">{name}</span>
                                  {isMakeup && (
                                    <span className="inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200">
                                      Make-up
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 sm:justify-center">
                                <span className="w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:hidden">
                                  Attend.
                                </span>
                                <AttendanceToggle
                                  current={current}
                                  loading={markingRowId === row.id}
                                  disabled={markingRowId != null && markingRowId !== row.id}
                                  onMark={(status) => void handleMarkStudent(lesson.id, row, status)}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}

                  {allowSelfCheckIn && (
                    <div className="border-t border-border bg-muted/10 px-4 py-4 sm:px-6">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <ClipboardCheck
                            className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                            aria-hidden
                          />
                          Your attendance
                        </div>
                        {loadingSelf ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
                        ) : (
                          <CoachSelfCheckInActions
                            rosterCount={rosterRows.length}
                            markedCount={markedStudentCount}
                            checkedIn={selfAttendance?.checkedIn ?? false}
                            checkingIn={checkingInLessonId === lesson.id}
                            selfCheckInAvailable={selfAttendance?.selfCheckInAvailable ?? false}
                            selfCheckInLocked={selfAttendance?.selfCheckInLocked ?? false}
                            onCheckIn={() => handleSelfCheckIn(lesson.id, 'attended')}
                            onUndo={() => handleSelfCheckIn(lesson.id, 'scheduled')}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
